import { useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { realtimeNotificationsService } from '../services';
import {
  clearStoredFcmToken,
  getStoredFcmToken,
  requestFcmToken,
  storeFcmToken,
} from '../realtime/fcm';

const PLATFORM = 'web' as const;

export const unregisterStoredFcmToken = async (): Promise<void> => {
  const token = getStoredFcmToken();
  if (!token) return;

  try {
    await realtimeNotificationsService.unregisterFcmToken(token);
  } catch {
    // Best effort cleanup.
  } finally {
    clearStoredFcmToken();
  }
};

export const useFcmRegistration = (): void => {
  const { user, isAuthenticated, isLoading } = useAuth();

  useEffect(() => {
    if (isLoading || !isAuthenticated || !user?.id) return;

    let isCancelled = false;

    const syncToken = async () => {
      const token = await requestFcmToken();
      if (!token || isCancelled) return;

      const currentStored = getStoredFcmToken();

      if (currentStored && currentStored !== token) {
        try {
          await realtimeNotificationsService.unregisterFcmToken(currentStored);
        } catch {
          // Continue registration even if old token cleanup fails.
        }
      }

      // Always register (even if token unchanged) so the backend re-activates
      // any token that was deactivated due to a prior FCM delivery failure.
      await realtimeNotificationsService.registerFcmToken({
        token,
        platform: PLATFORM,
        userAgent: navigator.userAgent,
      });
      storeFcmToken(token);
    };

    void syncToken();

    return () => {
      isCancelled = true;
    };
  }, [isAuthenticated, isLoading, user?.id]);
};

